import { z } from "zod";
//#region src/oauth.ts
/**
* RFC 8414 authorization-server metadata, as served at
* `/.well-known/oauth-authorization-server`. Only the members the platform
* kit consumes are typed; everything else passes through (non-strict, see
* header comment). `issuer`, `authorization_endpoint`, and `token_endpoint`
* are required because the PKCE kit cannot operate without them.
*/
const oauthServerMetadataSchema = z.looseObject({
	issuer: z.string().min(1),
	authorization_endpoint: z.string().min(1),
	token_endpoint: z.string().min(1),
	jwks_uri: z.string().min(1).optional(),
	registration_endpoint: z.string().min(1).optional(),
	scopes_supported: z.array(z.string()).optional(),
	response_types_supported: z.array(z.string()).optional(),
	grant_types_supported: z.array(z.string()).optional(),
	token_endpoint_auth_methods_supported: z.array(z.string()).optional(),
	revocation_endpoint: z.string().min(1).optional(),
	code_challenge_methods_supported: z.array(z.string()).optional()
});
/**
* RFC 9728 protected-resource metadata, served at a fixture-local path and
* advertised absolutely via the `WWW-Authenticate: Bearer
* resource_metadata="…"` challenge (AD-9). Non-strict (see header comment).
*
* ⛔ `scopes_supported` is OPTIONAL **here** because this schema PARSES
* documents that other parties serve — RFC 9728 §2 makes the member OPTIONAL,
* so tightening it would make this parser reject conformant third-party
* documents. That is a bug, not a hardening. It is nonetheless a **hard
* expectation of every Zion PRODUCER**: a Zion PRM document without
* `scopes_supported` is issue #498 — clients cache the document, then request
* no scope or a guessed one and are denied for a reason the document says
* should work.
*
* ⚠️ **The type permitting the field is not the field being served.** The
* `@zion/sdk-nestjs` kit (`ProtectedResourceController`) has always been able
* to emit `scopes_supported` under this type and never did — which is how two
* design documents concluded the kit already served it. If you are adding a
* producer, assert on the **emitted body**, never on the type.
*
* The Zion producers, named:
*  - CP's `/.well-known/oauth-protected-resource` (R4.5 WS-4). Its `scopes_supported` is the
*    cp-auth-api `CP_DELEGABLE_SCOPES`, built at load from `ALL_CP_PERMISSIONS` (spine R45-04).
*  - DP core-api's Host-resolved PRM endpoint (R4.5 WS-8) — **this is what
*    closes #498.**
*  - The kit's `OAuthProtectedResourceModuleOptions.scopesSupported` option
*    (R4.5 WS-2 CG-4) is the third path; it reaches no live fixture in this
*    release.
*/
const protectedResourceMetadataSchema = z.looseObject({
	resource: z.string().min(1),
	authorization_servers: z.array(z.string().min(1)).optional(),
	jwks_uri: z.string().min(1).optional(),
	scopes_supported: z.array(z.string()).optional(),
	bearer_methods_supported: z.array(z.string()).optional(),
	resource_name: z.string().optional(),
	resource_documentation: z.string().optional()
});
/**
* RFC 6749 §5.1 token-endpoint success response (snake_case wire shape).
* `scope` is the space-delimited granted-scope string — RFC 6749 §3.3
* permits the server to grant a NARROWER set than requested, so kits MUST
* honor this field when present rather than assuming requested == granted.
* Non-strict (see header comment).
*/
const oauthTokenResponseSchema = z.looseObject({
	access_token: z.string().min(1),
	token_type: z.string().min(1),
	expires_in: z.number().int().optional(),
	refresh_token: z.string().min(1).optional(),
	scope: z.string().optional()
});
/**
* Every OAuth error code the platform emits or handles:
* RFC 6749 §4.1.2.1 (authorization endpoint) + §5.2 (token endpoint),
* RFC 8707 `invalid_target` (resource indicator mismatch, AD-6), and
* RFC 6750 §3.1 `insufficient_scope` (bearer challenge on scoped routes).
*/
const OAUTH_ERROR_CODES = [
	"invalid_request",
	"invalid_client",
	"invalid_grant",
	"unauthorized_client",
	"unsupported_grant_type",
	"unsupported_response_type",
	"invalid_scope",
	"access_denied",
	"server_error",
	"temporarily_unavailable",
	"invalid_target",
	"insufficient_scope"
];
/**
* RFC 6749 §5.2 error envelope (`{error, error_description}` — the Nest
* default envelope is non-conformant, hence core-api's oauth error filter).
* Non-strict (see header comment): clients tolerate extension members.
*/
const oauthErrorResponseSchema = z.looseObject({
	error: z.enum(OAUTH_ERROR_CODES),
	error_description: z.string().optional(),
	error_uri: z.string().optional(),
	state: z.string().optional()
});
/**
* How a client came to exist, and therefore which trust tier it sits in.
*
*  - `'admin'`   — a human pre-registered it through the admin surface.
*  - `'dynamic'` — it self-registered via RFC 7591 Dynamic Client
*    Registration. **Everything it supplied is untrusted display data**:
*    `clientName` is up to 200 characters of attacker-controlled text that a
*    human will read on the consent screen.
*
* ⛔ Required, never optional, on every view that carries it. All three
* consumers BRANCH on it — the consent screen to render the "Unverified —
* this application registered itself" anti-impersonation marker, the admin
* client list to render the trust badge and to suppress an edit form that
* would silently no-op, and the user's connected-apps list to carry the same
* marker into the KEEP-or-revoke decision. An optional field lets a producer
* omit the discriminator that drives a security warning, and the omission
* renders as the SAFE tier.
*
* One schema, used by all three views on purpose: they carry the same
* discriminator, so a second copy could only ever drift — and the drift
* direction is "one surface stops warning".
*/
const oauthRegistrationTypeSchema = z.enum(["admin", "dynamic"]);
/**
* Consent-page view of a pending authorization request
* (`GET /v1/auth/oauth/authorize/:id`). Scope descriptions are
* server-resolved — from `ORG_PERMISSION_CATALOG` for legacy org-scope
* clients, from `release.app_permission_declarations` for app-bound clients
* (#494 AD-4); an unknown slug passes through with only `slug` populated.
* `held` reports whether the consenting user currently holds the permission
* so the page can disable Approve up front instead of failing after the
* click.
*/
const authorizeRequestViewSchema = z.object({
	id: z.uuid(),
	clientId: z.string().min(1),
	/**
	* The client's self-declared display name — **untrusted for a `dynamic`
	* client** (see `oauthRegistrationTypeSchema`). Renderers must treat it as
	* plain text and must never let it stand alone as the identity of the party
	* being consented to; that is what `redirectHost` is for.
	*/
	clientName: z.string().min(1),
	/**
	* The validated redirect URI's host, **port included** — `URL.host`, NOT
	* `URL.hostname`. Never the full URI (no scheme, path, query or fragment).
	*
	* Rendered prominently ("You'll be returned to {host}") because the host is
	* the part of a consent screen an attacker cannot forge — it is bounded by
	* the per-org DCR redirect-host allowlist — while the display name is the
	* part they can.
	*
	* ⛔ **The port is deliberate; do not "fix" this to `hostname`.** The human
	* is being asked where the authorization code will be sent, and for a
	* loopback client the port is the only distinguishing detail. `URL.host` is
	* a strict superset of `URL.hostname` and costs nothing: WHATWG omits the
	* scheme's default port, so `https://evil.com:443/cb` still renders
	* `evil.com` (no padding trick), and URL parsing strips userinfo either way
	* (`https://claude.ai@evil.com/cb` → `evil.com`).
	*
	* ⚠️ **Deliberate asymmetry, and it will look like a bug:** the redirect-host
	* **allowlist MATCHES on `hostname`** (a port is not part of the trust
	* decision — one host, one policy entry) while the consent screen
	* **DISPLAYS `host`**. Someone will try to reconcile the two "for
	* consistency". Both directions are wrong, and they are not symmetric:
	*
	*  - ⛔ **Making the ALLOWLIST port-sensitive is the direction to guard.**
	*    It does not fail open per-registration — an entry `claude.ai` simply
	*    stops matching `claude.ai:8443`, which is a refusal. The damage is
	*    second-order and worse: a loopback client picks an ephemeral port, so
	*    every port would need its own row, and the pressure that creates is
	*    toward a wildcard or a deliberately over-broad entry — a real widening,
	*    entered by an admin who was only trying to make a legitimate client
	*    work. The policy surface must stay one row per host.
	*  - Making the DISPLAY `hostname` is milder but still a regression: it hides
	*    the one detail distinguishing two clients on the same loopback host,
	*    which is exactly when a user needs it.
	*/
	redirectHost: z.string().min(1),
	/** Trust tier of the requesting client — drives the unverified marker. */
	registrationType: oauthRegistrationTypeSchema,
	orgName: z.string().min(1),
	/**
	* Name of the bound app (joined from `release.apps_catalog`, #494 AD-4)
	* — present only for app-bound clients; absent on legacy org-scope
	* clients; `''` on a catalog miss (the AD-15 fail-visible sentinel,
	* matching `oauthClientSummarySchema.appName` semantics).
	*/
	appName: z.string().optional(),
	/**
	* #1237 — the EXACT canonical RFC 8707 resource this authorization is bound
	* to. Present only for an app-scoped request; absent for org-scope (where
	* the resource is the org issuer and carries no information a user needs).
	*
	* ⛔ **The consent screen must render this verbatim and must not truncate it
	* in a way that hides the host or the path.** A dynamically-registered
	* client is self-asserted and unverified; the resource is the one field that
	* says *what* it is being granted access to, and an abbreviated URL is
	* precisely how a lookalike host would slip past a user who is otherwise
	* reading carefully.
	*
	* Derived server-side from the registered-OAuth-endpoint map
	* (`auth.oauth_clients.mcp_url` → `app_id`), never from anything the client
	* asserted about itself.
	*/
	resource: z.string().url().max(2048).optional(),
	scopes: z.array(z.object({
		slug: z.string().min(1),
		label: z.string().optional(),
		description: z.string().optional(),
		group: z.string().optional(),
		held: z.boolean().optional()
	}).strict())
}).strict();
/**
* Connected-apps view of a delegated grant (`GET /v1/auth/oauth/grants`).
* One entry per grant FAMILY (the live head), never per generation row.
*
* This is the **user's own review surface** for what they have granted, and it
* renders `clientName` — which for a `dynamic` client is up to 200 characters
* of attacker-controlled text, read while deciding whether to keep granting
* access. It therefore carries the same trust discriminator the consent screen
* does: marking the impersonation risk at the moment of consent and then
* dropping it forever afterwards would leave the revoke decision less informed
* than the grant decision.
*/
const oauthGrantSummarySchema = z.object({
	id: z.uuid(),
	clientId: z.string().min(1),
	/**
	* The client's self-declared display name — **untrusted for a `dynamic`
	* client.** Render as plain text; pair it with `registrationType`.
	*/
	clientName: z.string().min(1),
	/**
	* Trust tier of the client this grant was issued to — drives the
	* self-registered marker on the connected-apps list. Required for the same
	* reason as on the other two views: an optional discriminator lets a
	* producer omit the field that drives a security marker, and the omission
	* renders as the SAFE tier.
	*/
	registrationType: oauthRegistrationTypeSchema,
	scopes: z.array(z.string()),
	createdAt: z.string().datetime(),
	lastUsedAt: z.string().datetime().nullable()
}).strict();
/**
* Admin view of a registered OAuth client
* (`GET /v1/admin/oauth-clients`). `grantCount` counts distinct live grant
* families — surfaced in the destructive-delete confirm copy.
*/
const oauthClientSummarySchema = z.object({
	id: z.uuid(),
	clientId: z.string().min(1),
	name: z.string().min(1),
	redirectUris: z.array(z.string()),
	/**
	* The client's per-client org-scope delegation ceiling — **NULLABLE, and
	* `null` does NOT mean "no scopes."**
	*
	* ⛔ `null` means *"no per-client policy — the ceiling is the LIVE set"*
	* (spine D-4), and it is the only legal value for a `dynamic` client: a
	* self-registered client stores no ceiling, because a copied snapshot of the
	* catalog silently holds the client to the old set forever after the catalog
	* moves. A non-null array is real per-client policy, a human deliberately
	* narrowing one pre-registered `admin` client.
	*
	* ⇒ Renderers **must branch on `null`** and say so ("resolved at consent"),
	* never fall through to the empty-array path. `[]` and `null` are different
	* facts and the empty chip list reads as "this client can do nothing", which
	* is the exact opposite of the truth — a dynamic client's ceiling is the
	* widest one there is. (Its *reach* is still exactly one human's authority:
	* consent re-validates every scope against the consenting user.)
	*
	* ⛔ Nothing may read this field directly to compute an effective ceiling —
	* that is `effectiveCeiling(client)`'s job, which interprets `null` as the
	* live set by identity and a non-null array as `policy ∩ live`.
	*/
	allowedScopes: z.array(z.string()).nullable(),
	/**
	* Trust tier — `'admin'` (a human pre-registered it) or `'dynamic'` (it
	* self-registered via RFC 7591). See `oauthRegistrationTypeSchema`: the
	* admin list badges it and treats `dynamic` rows as read-only except
	* Disable and Delete, because a `dynamic` client has no admin-editable
	* policy by construction.
	*/
	registrationType: oauthRegistrationTypeSchema,
	/**
	* The `release.apps_catalog` UUID the client is bound to (#494 AD-1).
	* `null` for legacy (grandfathered) org-scope clients.
	*/
	appId: z.uuid().nullable(),
	/**
	* Bound app's display name, joined server-side from `release.apps_catalog`
	* to avoid an N+1 in the client list (#494 §2 SDK item 1c). `null` for
	* legacy org-scope clients; `''` on a catalog miss (fail-visible, AD-15
	* convention).
	*/
	appName: z.string().nullable(),
	/** The client's app-permission delegation ceiling (#494 AD-2). */
	allowedAppPermissions: z.array(z.string()),
	/**
	* #494 follow-up (Dave, 2026-06-12): the app's MCP endpoint URL, entered
	* once by the admin at client creation so the rendered `claude mcp add`
	* command is fully copy-pasteable (the platform cannot derive it —
	* apiPaths are fixture-declared and not synced to the DP). null = unset
	* (shell renders the `<mcp-url>` placeholder).
	*
	* Deliberately LENIENT (bare string) like every other summary field
	* (cf. `name` above vs the create DTO's `.min(1)`): the create/update
	* DTOs are the validation gate; the read path renders stored reality —
	* a strict response schema would turn one bad legacy row into a failed
	* list call for every SDK consumer.
	*/
	mcpUrl: z.string().nullable(),
	disabled: z.boolean(),
	grantCount: z.number().int().nonnegative(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime()
}).strict();
/**
* The client-DTO scope vocabulary is deliberately DUAL (#494 AD-3), and
* since #1033 the two halves have DIFFERENT grammars — so they get two
* schemas. A single shared schema across two vocabularies is exactly how
* these drift.
*
* ⛔ **DO NOT "tidy" these two back into one.** They were one schema until
* #1033, applied to all THREE of the fields below, and exactly ONE of them
* — `updateOAuthClientDtoSchema.allowedScopes`, the org-catalog ceiling —
* was widened. Collapsing them re-widens the app-declaration grammar
* silently. It would not fail a test either: `validateAppBinding` re-checks
* `PERMISSION_SLUG_PATTERN` server-side regardless, so the damage surfaces
* as a confusing 400 at runtime, not a red suite.
*
* ⚠️ Five near-copies of this grammar exist across the repo and their
* divergence is tracked in **#1036** (the release-engine copy already allows
* a leading digit). Read that issue before unifying anything here.
*
* Common to both: wildcards are structurally impossible (`*` is outside
* `[a-z0-9-]`), so they are FORBIDDEN in `allowed_scopes`,
* `allowed_app_permissions`, and `scp` (AD-4).
*/
/**
* App-permission KEYS (e.g. `perf:read`) — strictly 2-segment, the same
* shape as `PERMISSION_KEY_PATTERN` in `./permission-key.js`. Membership in
* the bound app's `release.app_permission_declarations` is enforced
* server-side (`validateAppBinding`, which re-checks the slug pattern
* regardless — this arm is belt-and-braces).
*
* ⛔ Do NOT widen this to match the org-scope arm below. App keys are
* provably disjoint from the org catalog (`isReservedOrgPermissionKey`
* blocks `org:`/`platform:` from ever becoming an app key), and nothing in
* the app-declaration path needs N≥3 segments.
*/
const oauthAppPermissionKeySchema = z.string().regex(/^[a-z][a-z0-9-]*:[a-z][a-z0-9-]*$/);
/**
* Org-permission SLUGS (e.g. `dlq:read`, `org:llm-credentials:manage`) for
* legacy org-scope clients — membership in `ALL_ORG_PERMISSIONS` is
* enforced server-side (`validateScopes`), which is the real gate; this is
* a lexical filter in front of it.
*
* ⚠️ #1033: N≥3 segments are admitted because the org catalog contains
* them. The 2-segment form here previously made `org:llm-credentials:manage`
* **ungrantable** — `PATCH /v1/admin/oauth-clients/:id
* { allowedScopes: ['org:llm-credentials:manage'] }` 400'd at the validation
* pipe even though `validateScopes` accepts it as a catalog member. Keeping
* this arm narrow while the DP's `/authorize` parser was widened would have
* meant advertising a scope that can never be granted — the same
* advertise/accept split as #1033, one layer up.
*/
const oauthOrgScopeSlugSchema = z.string().regex(/^[a-z][a-z0-9-]*(?::[a-z][a-z0-9-]*)+$/);
/**
* ⭐ **#1257 F4 — the ONE `mcp_url` validation, shared by create AND update.**
*
* `mcp_url` is not a free-form link: it is an **RFC 8707 / RFC 9728 resource
* identifier**, and the DP matches an incoming `resource` against it through
* `canonicalResourceUrl` (core-api `oauth/resource-url.ts`). That reader refuses
* a value that is not `http(s)`, that carries embedded credentials, or that
* carries a query or fragment — RFC 9728 §3.1 path-insertion can only carry path
* SEGMENTS, so such a value can never be discovered.
*
* ⛔ **Before this refinement the refusal happened only at the READING end, and
* silently.** A value like `https://org.example/mcp?x=1` registered 2xx, and then
* `canonicalResourceUrl` returned `null` for it forever — so
* `clientRegistersResource` matched NOTHING, every `/authorize` and every token
* exchange answered `invalid_target` behind the deliberately uniform
* `RESOURCE_REFUSED_DESCRIPTION`, and the client vanished from the RFC 9728
* protected-resource document. Neither the admin nor the client got a
* distinguishing signal. The client was permanently, invisibly dead.
*
* ⇒ Refuse it **here, loudly, at registration**, where the caller is an
* authenticated admin and a specific message is the right answer. (The vagueness
* rule is a control on the `@Public()` `/authorize` route, where a per-reason
* string would be an enumeration oracle. It does not apply to an authenticated
* admin write.)
*
* ⚠️ **All three arms were ALREADY non-functional**, not just the query/fragment
* one — verified consumer by consumer: `resource-url.ts:193`,
* `oauth.service.ts:593/606`, `oauth-token.service.ts:202` and
* `protected-resource-metadata.service.ts` every one route `mcp_url` through
* `canonicalResourceUrl`/`canonicalizeResource`. So this narrows nothing that
* ever worked; it converts a silent permanent failure into a 400.
*
* ⛔ **This predicate is a DUPLICATE of `canonicalResourceUrl`'s reject set, and
* that is a deliberate, tested compromise — not an oversight.** `@zion/sdk-contracts`
* is a DEPENDENCY of core-api, so importing the reader here would be a dependency
* cycle. The duplication is pinned by an equivalence test over a shared corpus in
* `core-api/src/oauth/__tests__/mcp-url-producer-consumer-agreement.spec.ts`,
* which fails if these two ever disagree on ANY input — the same anti-drift shape
* #1257 used to close the original two-answer divergence. If the reader's rule
* changes, that test reds and this must follow.
*
* The structural fix (hoist `canonicalResourceUrl` into this package — it is a
* pure, import-free function, so nothing blocks it) is tracked separately; doing
* it would delete this predicate and the test in favour of one definition.
*/
function isUsableResourceIdentifier(raw) {
	let url;
	try {
		url = new URL(raw);
	} catch {
		return false;
	}
	if (url.protocol !== "https:" && url.protocol !== "http:") return false;
	if (url.username !== "" || url.password !== "") return false;
	if (url.search !== "" || url.hash !== "") return false;
	return true;
}
/**
* The single `mcp_url` schema. ⛔ Used by BOTH the create and the update DTO on
* purpose: two independently-written `.refine()` calls are exactly how a producer
* develops two answers, which is the defect class this whole change closes.
*
* `http` is admitted alongside `https` for local loopback dev, matching the
* redirect-URI loopback allowance and `canonicalResourceUrl`'s own scheme rule.
*/
const mcpUrlSchema = z.string().url().max(2048).refine(isUsableResourceIdentifier, { message: "mcpUrl must be an http(s) URL with no query string, fragment, or embedded credentials — it is an RFC 9728 resource identifier, which can carry only path segments" });
/**
* Create an app-bound public OAuth client (#494 AD-1/AD-2). v2 creates are
* app-bound-only: `allowedScopes` is deliberately ABSENT (strict DTO ⇒
* sending it 400s); platform-feature org-scope clients are deferred.
*/
const createOAuthClientDtoSchema = z.object({
	/** Immutable slug identifier — doubles as the wire `client_id`. */
	clientId: z.string().regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/),
	name: z.string().min(1),
	redirectUris: z.array(z.string().min(1)).min(1),
	/** The `release.apps_catalog` UUID this client is bound to (AD-1). */
	appId: z.uuid(),
	/**
	* The client's delegation ceiling — must be ⊆ the bound app's declared
	* permission keys (AD-2; server-validated against
	* `release.app_permission_declarations`).
	*/
	allowedAppPermissions: z.array(oauthAppPermissionKeySchema).min(1),
	/**
	* Optional MCP endpoint URL — completes the mcp-add command. See
	* {@link mcpUrlSchema}: `https` on real DPs, `http` for local loopback dev.
	*
	* ⚠️ This comment previously said *"any URL scheme is accepted on purpose"*.
	* That was false end-to-end (#1257 F4): a non-`http(s)` value registered 2xx
	* and was then refused by every consumer forever, because they all match
	* through `canonicalResourceUrl`. The two admitted schemes are exactly the two
	* the rationale actually named.
	*/
	mcpUrl: mcpUrlSchema.optional()
}).strict();
/**
* Update a registered OAuth client. `clientId` is immutable (absent here).
* `allowedAppPermissions` is a straight edit of the delegation ceiling —
* propagated to live grants via refresh-time re-narrowing (#494 AD-5).
* `allowedScopes` remains editable for legacy org-scope clients only.
*/
const updateOAuthClientDtoSchema = z.object({
	name: z.string().min(1).optional(),
	redirectUris: z.array(z.string().min(1)).min(1).optional(),
	allowedScopes: z.array(oauthOrgScopeSlugSchema).min(1).optional(),
	appId: z.uuid().optional(),
	allowedAppPermissions: z.array(oauthAppPermissionKeySchema).min(1).optional(),
	/**
	* Optional MCP endpoint URL; `null` clears it. Same {@link mcpUrlSchema} as
	* create — one predicate, so the two doors cannot drift apart. `.nullable()`
	* short-circuits the refinement, so clearing stays legal.
	*/
	mcpUrl: mcpUrlSchema.nullable().optional(),
	disabled: z.boolean().optional()
}).strict();
/**
* Add a host to the per-org DCR redirect-host allowlist.
*
* ⛔ `host` is an ENTRY, never a full URI — it carries no path, no port, no
* userinfo and no query. **Two forms are accepted**, and the difference is a
* security boundary, not a spelling preference:
*
*  - a **BARE HOST** — `"claude.ai"` (exact) or `"*.claude.ai"` (subdomains
*    only, never the apex). ⛔ A bare entry authorizes **https and loopback
*    http ONLY**; it does *not* also admit a private-use scheme on that host.
*  - a **SCHEME-BOUND HOST** — `"cursor://anysphere.cursor-mcp"`, for a native
*    client that claims its own URI scheme (RFC 8252 §7.1). It authorizes
*    **that scheme and that host only**; `*.` is not permitted with a scheme,
*    and the scheme may not be one of the six WHATWG *special* schemes
*    (`http`, `https`, `ws`, `wss`, `ftp`, `file`).
*
* ⭐ Both halves of that binding exist because three different schemes produce
* the IDENTICAL `new URL().hostname`: a scheme-blind entry would authorize
* strictly more than its author believed — `evil://anysphere.cursor-mcp/cb`
* against the intended host, and `cursor://claude.ai/cb` against an org that
* only ever approved the hosted https connector.
*
* Enforced by the grammar validator, not here — zod validates *shape*, the
* validator owns *policy* and the actionable reason strings.
*
* ⚠️ **BOTH PLANES, as of 2026-08-05.** An earlier revision of this comment said
* *"DP only, deliberately"* — that was true for about an hour and is now false.
* `cp-auth-api` carries the same scheme-bound grammar, so a `cursor://host` entry
* behaves identically on a control plane and a data plane (#1215, closed by the
* same PR that added the DP side).
*
* ⛔ The stale version of this note is worth remembering rather than just deleting:
* it was accurate when written and became a lie **without anyone editing it**, in
* the file a reader treats as the authoritative cross-language statement of this
* contract. That is the #1216 failure class applied to prose — a claim about a
* SIBLING plane decays whenever the sibling changes, and nothing fails when it
* does. If you scope a change to one plane, the divergence note you write is a
* debt with no due date.
*/
const createDcrRedirectHostSchema = z.object({
	/**
	* 253 is the maximum length of a DNS name; a leading `*.` wildcard fits
	* inside it, so the wildcard form needs no separate bound.
	*/
	host: z.string().min(1).max(253),
	/** Why this entry exists — read by the next admin during an incident. */
	description: z.string().max(500).optional(),
	/** Admit NEW registrations on this host. Defaults to `true` at the service. */
	enabled: z.boolean().optional()
}).strict();
/**
* Edit an allowlist entry.
*
* ⛔ **`host` is IMMUTABLE — absent BY CONSTRUCTION**, so `.strict()` rejects
* an attempt to change it. Editing a host in place turns "who approved this
* entry" into a lie against the audit trail: `createdByUserId` would still
* name the admin who approved the OLD value. Delete and re-create.
*/
const updateDcrRedirectHostSchema = z.object({
	/**
	* `.nullable()` so an admin can CLEAR the description; `.optional()` so
	* omitting it leaves the stored value alone. The two mean different things
	* and both are reachable.
	*/
	description: z.string().max(500).nullable().optional(),
	/** Registration-time only — see the section header. */
	enabled: z.boolean().optional()
}).strict();
/**
* Admin view of one allowlist entry (`GET /v1/admin/dcr-redirect-hosts`).
* `createdByUserId` is nullable because an entry can outlive the user who
* created it (or be seeded outside a user session).
*/
const dcrRedirectHostSummarySchema = z.object({
	id: z.string().uuid(),
	/**
	* Deliberately LENIENT (bare string), like every other summary field: the
	* create DTO plus the grammar validator are the write-time gate, and the
	* read path renders stored reality. A strict read schema would turn one bad
	* legacy row into a failed list call for every consumer.
	*/
	host: z.string(),
	description: z.string().nullable(),
	enabled: z.boolean(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	createdByUserId: z.string().uuid().nullable()
}).strict();
//#endregion
export { OAUTH_ERROR_CODES, authorizeRequestViewSchema, createDcrRedirectHostSchema, createOAuthClientDtoSchema, dcrRedirectHostSummarySchema, oauthClientSummarySchema, oauthErrorResponseSchema, oauthGrantSummarySchema, oauthServerMetadataSchema, oauthTokenResponseSchema, protectedResourceMetadataSchema, updateDcrRedirectHostSchema, updateOAuthClientDtoSchema };

//# sourceMappingURL=oauth.js.map